import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { guardarDestino } from '@/lib/destino';
import FundoEvento from '@/components/feature/FundoEvento';

// WhatsApp do Se Liga (quem gera e manda o link de nova senha).
const NUMERO_SELIGA = '5514996402835';

// Máscara de CPF enquanto digita: só números viram 000.000.000-00.
export function mascararCpf(valor: string): string {
  const d = valor.replace(/\D/g, '').slice(0, 11);
  if (!d) return '';
  if (d.length <= 3) return d;
  if (d.length <= 6) return `${d.slice(0, 3)}.${d.slice(3)}`;
  if (d.length <= 9) return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6)}`;
  return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
}

// CPF nunca vai inteiro: só os 5 últimos dígitos, no formato ***.***.123-45.
function cpfMascarado(digitos: string): string {
  const d = digitos.replace(/\D/g, '');
  if (d.length >= 6) {
    const meio = d.slice(6, 9).padStart(3, '0');
    const fim = d.slice(9, 11);
    return fim ? `***.***.${meio}-${fim}` : `***.***.${meio}`;
  }
  return '';
}

// Só números (sem @ e sem letra) é tratado como CPF.
function ehSoNumeros(valor: string): boolean {
  return valor.length > 0 && !/[a-zA-Z@]/.test(valor);
}

export default function RecuperarSenhaPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [conta, setConta] = useState('');

  const voltar = searchParams.get('voltar');
  useEffect(() => {
    if (voltar) guardarDestino(voltar);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const soNumeros = ehSoNumeros(conta);
  const valorCampo = soNumeros ? mascararCpf(conta) : conta;

  const identificacao = (() => {
    const limpo = conta.trim();
    if (!limpo) return 'não informei';
    if (ehSoNumeros(limpo)) return cpfMascarado(limpo) || 'não informei';
    return limpo;
  })();

  const textoWhatsapp = `Olá! Esqueci minha senha no Se Liga Católico. Minha conta: ${identificacao}`;
  const linkWhatsapp = `https://wa.me/${NUMERO_SELIGA}?text=${encodeURIComponent(textoWhatsapp)}`;

  return (
    <FundoEvento>
      <div className="flex min-h-[100dvh] items-center justify-center px-4 py-8">
        <div className="app-entra w-full max-w-[420px] overflow-hidden rounded-[16px] bg-white/95 backdrop-blur-md">
          <div className="p-6 sm:p-8">
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => navigate(-1)}
                aria-label="Voltar"
                className="flex h-11 w-11 shrink-0 cursor-pointer items-center justify-center rounded-full text-foreground-700 transition-colors hover:bg-background-100"
              >
                <i className="ri-arrow-left-line text-xl" aria-hidden />
              </button>
              <h1 className="text-[19px] font-extrabold leading-tight text-foreground-950">
                Esqueci minha senha
              </h1>
            </div>

            <p className="mt-4 text-[15px] leading-relaxed text-foreground-700">
              Peça uma nova senha pelo WhatsApp do Se Liga. Você recebe um link que vale 10 minutos.
            </p>

            <div className="mt-5">
              <label
                htmlFor="recuperar-conta"
                className="block text-[13px] font-semibold text-foreground-800"
              >
                Seu e-mail ou CPF (ajuda a achar sua conta)
              </label>
              <input
                id="recuperar-conta"
                name="conta"
                type="text"
                inputMode={soNumeros ? 'numeric' : 'text'}
                autoComplete="off"
                placeholder="maria@gmail.com ou 000.000.000-00"
                value={valorCampo}
                onChange={(e) => setConta(e.target.value)}
                className="mt-2 h-[52px] w-full rounded-[12px] border border-background-300 bg-white px-4 text-[15px] text-foreground-950 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
              />
              <p className="mt-2 text-[13px] leading-relaxed text-foreground-500">
                Se você digitar o CPF, ele vai só com os últimos números. Nada inteiro é enviado.
              </p>
            </div>

            <a
              href={linkWhatsapp}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-6 flex h-[52px] w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-[12px] bg-[#25D366] px-4 text-[15px] font-bold text-white transition-colors hover:brightness-95"
            >
              <span className="flex h-5 w-5 items-center justify-center">
                <i className="ri-whatsapp-line text-lg" aria-hidden />
              </span>
              Pedir nova senha no WhatsApp
            </a>

            <p className="mt-4 text-[13px] leading-relaxed text-foreground-500">
              Atendemos no horário comercial. O link chega no seu WhatsApp.
            </p>

            <div className="mt-5 border-t border-background-200 pt-4">
              <Link
                to="/entrar"
                className="text-[13px] font-semibold text-primary-700 transition-colors hover:text-primary-800"
              >
                Voltar para entrar
              </Link>
            </div>
          </div>
        </div>
      </div>
    </FundoEvento>
  );
}